import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { config } from '../../config';

export default function RechargePrincipal() {
  const [loading, setLoading] = useState(true);
  const [moyenPaiement, setMoyenPaiement] = useState(null);
  const [responsable, setResponsable] = useState(null);
  const [codeEnvoi, setCodeEnvoi] = useState('');
  const [recuImage, setRecuImage] = useState(null);
  const [recuPreview, setRecuPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const API_BASE = config.apiBase;
  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    const fetchMoyenPaiement = async () => {
      try {
        const token = getToken();
        const response = await fetch(`${API_BASE}/etudiant/recharge/get_moyen_paiement.php?type=principal`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success) {
          setMoyenPaiement(data.moyen_paiement ? {
            ...data.moyen_paiement,
            qr_image_url: config.normalizeQrCode(data.moyen_paiement.qr_image_url)
          } : null);
          setResponsable(data.responsable);
        } else {
          toast.error(data.message);
          navigate('/etudiant/dashboard');
        }
      } catch (error) {
        toast.error('Erreur de chargement');
      } finally {
        setLoading(false);
      }
    };
    fetchMoyenPaiement();
  }, [navigate]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Veuillez sélectionner une image');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image trop volumineuse (max 5 Mo)');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setRecuImage(reader.result);
        setRecuPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!codeEnvoi.trim()) {
      toast.error('Code d\'envoi requis');
      return;
    }
    if (!recuImage) {
      toast.error('Veuillez joindre la capture du reçu');
      return;
    }

    setSubmitting(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/etudiant/recharge/demander_recharge_principal.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          code_envoi: codeEnvoi,
          recu_base64: recuImage,
          id_technicien: responsable.id_utilisateur
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Demande envoyée avec succès');
        setCodeEnvoi('');
        setRecuImage(null);
        setRecuPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 rounded-full border-4 border-emerald-100"></div>
          <div className="absolute inset-0 rounded-full border-4 border-emerald-600 border-t-transparent animate-spin"></div>
        </div>
        <p className="mt-4 font-medium text-gray-500 animate-pulse">Chargement des options de paiement...</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6 antialiased text-gray-800">
      
      {/* En-tête de page moderne en Vert */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between border-b border-gray-100 pb-5 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight bg-gradient-to-r from-emerald-700 to-teal-600 bg-clip-text text-transparent">
            💰 Recharge Compte Principal
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Approvisionnez votre solde en toute sécurité via vos opérateurs mobiles.
          </p>
        </div>
        
        {/* Encadré Responsable Technique épuré Vert */}
        {responsable && (
          <div className="flex items-center gap-3 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100/80 rounded-2xl p-3 shadow-sm max-w-sm">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center text-white font-bold text-lg shadow-sm">
              {responsable.prenom?.[0]}{responsable.nom?.[0]}
            </div>
            <div className="text-xs sm:text-sm">
              <p className="text-gray-400 font-medium uppercase tracking-wider text-[10px]">Support Technique</p>
              <p className="font-semibold text-emerald-900">{responsable.prenom} {responsable.nom}</p>
              <p className="text-emerald-700 font-medium text-xs">{responsable.telephone || 'N/A'}</p>
            </div>
          </div>
        )}
      </div>

      {/* Grid d'action principale */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        
        {/* Colonne Gauche : Instructions & QR Code */}
        <div className="md:col-span-2 space-y-6">
          {moyenPaiement ? (
            <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xl shadow-gray-100/50 flex flex-col items-center text-center transition-all hover:shadow-2xl hover:shadow-emerald-100/40">
              <span className="px-3 py-1 bg-emerald-100 text-emerald-700 font-semibold rounded-full text-xs uppercase tracking-wide mb-4">
                {moyenPaiement.type_paiement}
              </span>
              
              {moyenPaiement.qr_image_url ? (
                <div className="relative group p-3 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 mb-4">
                  <img 
                    src={moyenPaiement.qr_image_url} 
                    alt="QR Code de paiement" 
                    className="w-44 h-44 object-contain rounded-xl bg-white transition-transform group-hover:scale-105 duration-300" 
                  />
                </div>
              ) : (
                <div className="w-44 h-44 bg-gray-50 rounded-2xl flex items-center justify-center border-2 border-dashed border-gray-200 mb-4 text-4xl">
                  📱
                </div>
              )}

              <div className="space-y-1 w-full">
                <p className="text-xs text-gray-400 font-medium">Numéro de transfert</p>
                <p className="text-xl font-bold tracking-wide text-gray-900 bg-gray-50 py-2 rounded-xl border border-gray-100">
                  {moyenPaiement.source_qr}
                </p>
              </div>

              <div className="mt-5 text-xs text-gray-500 space-y-2 bg-amber-50/70 border border-amber-100 p-3 rounded-xl text-left w-full">
                <p className="font-semibold text-amber-800 flex items-center gap-1.5">
                  ⚠️ Instructions importantes :
                </p>
                <ul className="list-disc pl-4 space-y-1 text-amber-950">
                  <li>Effectuez le transfert vers ce numéro exact.</li>
                  <li>Copiez précisément le <strong>Code d'envoi</strong> reçu par SMS.</li>
                  <li>Prenez une capture d'écran claire du reçu final.</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-2xl text-sm font-medium">
              Aucun moyen de paiement actif configuré pour le moment.
            </div>
          )}
        </div>

        {/* Colonne Droite : Formulaire */}
        <div className="md:col-span-3">
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xl shadow-gray-100/50">
            <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
              <span>📝</span> Valider ma transaction
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              
              {/* Input Code d'envoi */}
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700">
                  Code de transaction / Référence <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={codeEnvoi}
                  onChange={(e) => setCodeEnvoi(e.target.value)}
                  placeholder="Ex : PP260305.1204.A00123"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm transition-all text-sm font-medium"
                  required
                />
              </div>

              {/* Upload Dropzone Vert */}
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700">
                  Preuve de paiement (Capture d'écran) <span className="text-red-500">*</span>
                </label>
                
                <div
                  className={`border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all duration-200 flex flex-col items-center justify-center min-h-[180px]
                    ${recuPreview 
                      ? 'border-emerald-500 bg-emerald-50/20' 
                      : 'border-gray-300 hover:border-emerald-400 hover:bg-gray-50/50'
                    }`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {recuPreview ? (
                    <div className="relative group w-full max-w-[240px]">
                      <img 
                        src={recuPreview} 
                        alt="Aperçu reçu" 
                        className="mx-auto max-h-36 rounded-xl shadow-md object-contain border border-white" 
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center text-white text-xs font-semibold">
                        Changer l'image
                      </div>
                      <p className="text-xs text-emerald-700 font-semibold mt-2 flex items-center justify-center gap-1">
                        ✨ Image chargée avec succès
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto text-xl shadow-sm">
                        📸
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Parcourir ou glisser votre image</p>
                        <p className="text-xs text-gray-400 mt-0.5">Format supporté : PNG, JPG (Max 5 Mo)</p>
                      </div>
                    </div>
                  )}
                </div>
                <input 
                  ref={fileInputRef} 
                  type="file" 
                  accept="image/*" 
                  onChange={handleFileChange} 
                  className="hidden" 
                />
              </div>

              {/* Bouton de Soumission Vert */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full relative overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold py-3.5 px-4 rounded-xl shadow-lg shadow-emerald-200/50 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed text-sm tracking-wide mt-2"
              >
                {submitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Traitement en cours...</span>
                  </div>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    📤 Envoyer la demande de recharge
                  </span>
                )}
              </button>

            </form>
          </div>
        </div>

      </div>
    </div>
  );
}